import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import * as BABYLON from '@babylonjs/core'
import { Button, GradientText } from '@/design-system'
import { LogoMark } from '@/features/album/components/EgowallLogo'
import { useAuth } from '@/hooks/common'
import { usePublicGallery } from '../../hooks/usePublicGallery'
import { useStudioEngine } from '../../hooks/useStudioEngine'
import { useStudioRoom } from '../../hooks/useStudioRoom'
import { APP_NAME } from '@/utils/constants'
import { useStudioArts } from '../../hooks/useStudioArts'
import { useStudioLegacyRoom } from '../../hooks/useStudioLegacyRoom'
import { useStudioCameraRig } from '../../hooks/useStudioCameraRig'
import { useStudioTour } from '../../hooks/useStudioTour'
import { useOverlayProjection } from '../../hooks/useOverlayProjection'
import { getClassicFamilyForId } from '../../classicCatalog'
import { SCENE_PRESET_GEOMETRY } from '../../constants'
import { StudioPin } from '../../types'
import { pinsForArt, resolvePinWorldTuple } from '../../utils/studioPins'
import { StudioTourBar } from '../../components/StudioTourBar'
import { StudioOverlays } from '../../components/StudioOverlays'
import { StudioCanvasLightbox } from '../../components/StudioCanvasLightbox'
import { StudioLoadingVeil } from '../../components/StudioLoadingVeil'
import { GlassPanel, STUDIO_ACCENT, monoLabelSx, ResetGlyph } from '../../components/shared'

const Stage = styled(Box)({ position: 'fixed', inset: 0, overflow: 'hidden', background: '#0b0a10' })

const Canvas = styled('canvas')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  touchAction: 'none',
  display: 'block',
})

const CenterState = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
})

const Kbd = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  border: '1px solid var(--ds-night-border)',
  borderRadius: 5,
  padding: '2px 6px',
  color: 'var(--ds-night-ink)',
  background: 'rgba(244,243,246,0.06)',
})

const PresentHud = styled(GlassPanel)({
  position: 'absolute',
  left: '50%',
  bottom: 22,
  transform: 'translateX(-50%)',
  zIndex: 40,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 16px',
  borderRadius: 999,
  ...monoLabelSx,
  fontSize: 8.5,
  '@media (max-width: 720px)': {
    bottom: 110,
  },
})

const PresentHudSep = styled('span')({ width: 1, height: 16, flexShrink: 0, background: 'var(--ds-night-border)' })

/**
 * Guest's own "get back to where I started" — mirrors Studio3DScreen's
 * `PresentResetBtn` (Present mode's identical gap: no recenter once a
 * visitor drags/scrolls off the room's framed pose). Guests never see the
 * owner-only Studio editor's Pan/Zoom pills at all, so this is the *only*
 * reset available here, not a supplement to one.
 */
const PresentResetBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  width: 20,
  height: 20,
  borderRadius: '50%',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: 'var(--ds-night-ink-subtle)',
  transition: 'all 140ms ease',
  '&:hover': { background: 'rgba(244,243,246,0.1)', color: 'var(--ds-night-ink)' },
  '& svg': { display: 'block' },
})

const WalkPad = styled(Box)({
  position: 'absolute',
  left: 16,
  bottom: 22,
  zIndex: 41,
  display: 'none',
  gridTemplateColumns: '44px 44px 44px',
  gridTemplateRows: '44px 44px',
  gap: 6,
  pointerEvents: 'auto',
  '@media (hover: none), (pointer: coarse)': {
    display: 'grid',
  },
})

const WalkBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  width: 44,
  height: 44,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 16,
  fontWeight: 700,
  color: 'var(--ds-night-ink)',
  background: 'rgba(23,22,29,0.82)',
  border: '1px solid var(--ds-night-border)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  touchAction: 'none',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  '&:active': { background: 'rgba(190,157,253,0.28)', borderColor: 'rgba(190,157,253,0.55)' },
})

/* ── top chrome — thin, no editor controls; this is a viewer, not a studio ── */
const TopBar = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  background: 'linear-gradient(180deg, rgba(0,0,0,0.55), transparent)',
  pointerEvents: 'none',
})

const TbLeft = styled(Box)({ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 })

const TbMark = styled(Box)({
  width: 26,
  height: 26,
  borderRadius: 8,
  flexShrink: 0,
  background: 'rgba(255,255,255,0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const TbTitle = styled(Box)({ fontSize: 13.5, fontWeight: 700, letterSpacing: '-0.01em', color: '#fff' })
const TbSub = styled(Box)({ fontSize: 10.5, color: 'rgba(255,255,255,0.5)', marginTop: 1 })
const TbSubStrong = styled('b')({ color: 'rgba(255,255,255,0.78)', fontWeight: 650 })

const TbPill = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 12px',
  borderRadius: 100,
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.14)',
  fontSize: 11,
  fontWeight: 600,
  color: 'rgba(255,255,255,0.85)',
  pointerEvents: 'auto',
})

const TbPillBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 12px',
  borderRadius: 100,
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.14)',
  fontSize: 11,
  fontWeight: 600,
  color: 'rgba(255,255,255,0.85)',
  pointerEvents: 'auto',
  '&:hover': { background: 'rgba(255,255,255,0.14)' },
})

const TbRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  pointerEvents: 'auto',
  flexShrink: 0,
})

/* ── role-aware footer chrome ── */
const CtaBanner = styled(Box, { shouldForwardProp: (p) => p !== '$hidden' })<{ $hidden?: boolean }>(({ $hidden }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '16px 24px',
  paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
  background: 'linear-gradient(0deg, rgba(0,0,0,0.72), rgba(0,0,0,0.35) 70%, transparent)',
  transform: $hidden ? 'translateY(120%)' : 'translateY(0)',
  opacity: $hidden ? 0 : 1,
  pointerEvents: $hidden ? 'none' : 'auto',
  transition: 'transform 320ms cubic-bezier(.2,.7,.3,1), opacity 320ms cubic-bezier(.2,.7,.3,1)',
}))

const CtaCopy = styled(Box)({ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 })

const CtaAvatar = styled(Box)({
  width: 34,
  height: 34,
  borderRadius: '50%',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${STUDIO_ACCENT.coral}, ${STUDIO_ACCENT.gold})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
  fontWeight: 800,
  color: '#2a1608',
})

const CtaTextTitle = styled(Box)({ fontSize: 13.5, fontWeight: 700, color: '#fff' })
const CtaTextSub = styled(Box)({ fontSize: 11.5, color: 'rgba(255,255,255,0.6)', marginTop: 1 })

const BtnDismiss = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  cursor: 'pointer',
  width: 30,
  height: 30,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'rgba(255,255,255,0.5)',
  fontSize: 15,
  flexShrink: 0,
  '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.08)' },
})

const QuietPillWrap = styled(Box)({
  position: 'absolute',
  bottom: 22,
  right: 22,
  zIndex: 30,
  // Sit above the guest story strip when present.
  '@media (min-width: 721px)': { bottom: 88 },
})

const QuietPill = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 8px 8px 14px',
  borderRadius: 100,
  background: 'rgba(19,18,25,0.86)',
  border: '1px solid var(--ds-night-border)',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  fontSize: 11.5,
  fontWeight: 600,
  color: 'rgba(244,243,246,0.85)',
})

/* ── "designed in Studio" marketing moment — triggered by tapping a print,
   not persistent chrome. Deliberately not a data/inspector card (nothing
   editable to show a guest) — the print tap is the hook, the room's own
   craft is the pitch. ── */
const MomentScrim = styled(Box)({
  position: 'absolute',
  inset: 0,
  // Above StudioCanvasLightbox's zIndex 90 — the print-tap detail view and
  // the one-shot signup moment can fire from the same tap; the moment must
  // win the stack so it's never silently buried behind the lightbox.
  zIndex: 95,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: 'rgba(6,5,10,0.55)',
  backdropFilter: 'blur(2px)',
})

const MomentCard = styled(GlassPanel)({
  position: 'relative',
  width: '100%',
  maxWidth: 380,
  padding: '28px 26px 24px',
  borderRadius: 20,
  textAlign: 'center',
  background: 'rgba(21,19,28,0.92)',
  boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(190,157,253,0.18), 0 0 60px rgba(190,157,253,0.08)`,
})

const MomentIcon = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: '50%',
  margin: '0 auto 16px',
  background: `linear-gradient(135deg, ${STUDIO_ACCENT.violet}, ${STUDIO_ACCENT.cyan})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 18,
  color: '#0d0d0f',
})

const MomentTitle = styled(Box)({ fontSize: 16, fontWeight: 750, color: '#fff', letterSpacing: '-0.01em', marginBottom: 8 })
const MomentCopy = styled(Box)({ fontSize: 12.5, color: 'rgba(244,243,246,0.6)', lineHeight: 1.55, marginBottom: 22 })
const MomentActions = styled(Box)({ display: 'flex', flexDirection: 'column', gap: 8 })

const MomentClose = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  cursor: 'pointer',
  position: 'absolute',
  top: 10,
  right: 10,
  width: 28,
  height: 28,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'rgba(244,243,246,0.45)',
  fontSize: 14,
  '&:hover': { color: 'rgba(244,243,246,0.9)', background: 'rgba(244,243,246,0.08)' },
})

type Role = 'guest' | 'member' | 'owner'

// Shared shell for the "can't show you this gallery" states (private /
// not-found). By the time we know a link is locked, the backend has already
// withheld everything about it — no title, no scene — so the copy stays
// deliberately generic. Still a real marketing beat (the actual mark, a
// headline, a pitch, two CTAs), not a bare error page — a locked door is
// still a chance to sell the product.
function GuestGate({
  eyebrow,
  title,
  description,
  onGoHome,
  onBrowse,
}: {
  eyebrow: string
  title: string
  description: React.ReactNode
  onGoHome: () => void
  onBrowse: () => void
}) {
  return (
    <Box sx={{ textAlign: 'center', color: '#fff', maxWidth: 400, px: 3 }}>
      <LogoMark size={44} sx={{ margin: '0 auto 20px' }} />
      <GradientText variant='eyebrow'>{eyebrow}</GradientText>
      <Box sx={{ mt: '10px', fontSize: 24, fontWeight: 760, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
        {title}
      </Box>
      <Box sx={{ mt: '12px', fontSize: 13.5, color: 'rgba(244,243,246,0.6)', lineHeight: 1.6 }}>
        {description}
      </Box>
      <Box sx={{ mt: 3.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
        <Button variant='spectrum' size='lg' onClick={onGoHome}>
          Make your own — free <span aria-hidden>→</span>
        </Button>
        <Button variant='ghost' size='sm' onClick={onBrowse} sx={{ color: 'rgba(244,243,246,0.75)' }}>
          Browse public albums &amp; galleries
        </Button>
      </Box>
    </Box>
  )
}

/**
 * `/g/:shareId` — the real, public, unauthenticated guest render. Boots the
 * same Babylon hooks the authenticated Studio uses, straight into read-only
 * Present mode, with zero editor chrome. This replaced an earlier flat CSS
 * diorama that could never actually reflect the owner's real room/frames/
 * photos — this renders the same scene state Studio persists, so it's
 * automatically correct instead of re-mocked.
 *
 * Story pins are read-only overlays (hover/tap to expand) with a signup
 * nudge on reveal — guest authoring was decided against, not deferred (see
 * `onPinReveal` below). STORY tour reuses `useStudioTour` + `StudioTourBar`.
 * Mobile walk uses an on-screen pad when the pointer is coarse / no-hover.
 */
export function GuestGalleryScreen() {
  const { shareId = '' } = useParams<{ shareId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const gallery = usePublicGallery(shareId)
  const ready = gallery.status === 'ready'

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { scene } = useStudioEngine(canvasRef, ready)
  const room = useStudioRoom(scene, gallery.scene.room)
  const { width: roomW, height: roomH, depth: roomD } = room.room
  const roomDims = useMemo(() => ({ width: roomW, height: roomH, depth: roomD }), [roomW, roomH, roomD])
  const mode = room.room.mode || 'studio'
  const artsApi = useStudioArts(scene, gallery.scene.arts, roomDims, mode)
  const legacy = useStudioLegacyRoom(
    scene,
    mode === 'classic' ? room.room.classicTemplateId : undefined,
    room.room.wallColor,
    room.room.floorColor,
    room.room.ceilColor,
    artsApi.arts,
    artsApi.selectedId,
  )
  // This route is always full-bleed (no inspector panel eating canvas
  // width like the authenticated editor), so it can afford to fill the
  // frame with the print-bearing wall itself instead of the editor's more
  // conservative default (which fits the room's *full* loaded footprint —
  // including its side walls, often far wider than the prints) — see
  // `useStudioCameraRig`'s `classicZoom` doc.
  const cameraRig = useStudioCameraRig(scene, canvasRef, mode === 'classic' ? legacy.framing : null, mode === 'classic', 0.62)
  const projection = useOverlayProjection(scene, containerRef)
  const guestPins = gallery.scene.pins
  const showPins = room.room.showPins !== false
  /** Hung prints only — lightbox filmstrip skips Classic slot placeholders. */
  const lightboxArts = useMemo(() => artsApi.arts.filter((a) => !a.isPlaceholder && a.img), [artsApi.arts])
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const lightboxPins = useMemo(() => {
    if (lightboxIndex == null) return []
    const art = lightboxArts[lightboxIndex]
    if (!art) return []
    return pinsForArt(art, guestPins, room.room)
  }, [lightboxIndex, lightboxArts, guestPins, room.room])
  const artsRef = useRef(artsApi.arts)
  artsRef.current = artsApi.arts
  const pinsRef = useRef(guestPins)
  pinsRef.current = guestPins
  const roomRef = useRef(room.room)
  roomRef.current = room.room
  const getPinPos = useCallback((pin: StudioPin) => {
    return new BABYLON.Vector3(...resolvePinWorldTuple(pin, artsRef.current, pinsRef.current, roomRef.current))
  }, [])
  const presencePos = useCallback(() => null as BABYLON.Vector3 | null, [])

  // Present, permanently — there's no editor to fall back to on this route.
  // Classic rooms load their `.babylon` file asynchronously (see
  // useStudioLegacyRoom) — `legacy.framing` only resolves once that finishes.
  // Calling enterPresent() (which resets the orbit camera) before then found
  // `classicFramingRef.current` still null and fell through to the
  // *procedural* HOME_CAMERA reset target/radius, which has nothing to do
  // with this room's real geometry — then got stuck animating toward that
  // wrong pose (camGoalRef persists across frames), fighting the correct
  // pose useStudioCameraRig sets the instant framing actually does resolve.
  // Net effect: camera lands off in a random corner near the ceiling with
  // no floor in view. Wait for framing on Classic; Studio-mode rooms build
  // synchronously so there's nothing to wait for.
  const enteredPresentRef = useRef(false)
  const presentGateReady = mode !== 'classic' || !!legacy.framing
  useEffect(() => {
    if (!ready || !presentGateReady || enteredPresentRef.current) return
    enteredPresentRef.current = true
    cameraRig.enterPresent()
  }, [ready, presentGateReady, cameraRig])

  // Dollhouse cutaway in guest Orbit — same live getter as Studio (walk stays solid).
  useEffect(() => {
    room.registerActiveCamera(cameraRig.getActiveCameraInfo)
  }, [room, cameraRig.getActiveCameraInfo])

  const isOwner = !!user?.userID && !!gallery.topic?.userID && String(user.userID) === String(gallery.topic.userID)
  const role: Role = isOwner ? 'owner' : user?.userID ? 'member' : 'guest'

  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [moment, setMoment] = useState(false)
  const [momentReason, setMomentReason] = useState<'print' | 'pin'>('print')
  const momentShownRef = useRef(false)

  // Guests can't author pins themselves — the scene is one shared JSON note
  // per gallery with no per-pin write path, so a concurrent guest write
  // would race the owner's. Opening someone else's note is exactly the
  // moment to offer signup instead, once per session.
  const onPinReveal = useCallback(() => {
    if (role !== 'guest' || momentShownRef.current) return
    momentShownRef.current = true
    setMomentReason('pin')
    setMoment(true)
  }, [role])

  const flyToArt = useCallback(
    (id: string, artHint?: { side: -1 | 0 | 1; w: number }) => {
      const art = artHint ?? artsApi.arts.find((a) => a.id === id)
      const center = artsApi.getCenterFor(id)
      if (art && center) cameraRig.flyTo({ side: art.side, center, width: art.w })
    },
    [artsApi, cameraRig],
  )

  // Bare pointer-pick, deliberately simpler than useStudioTools — this
  // viewer has exactly one interaction (tap a print), no tool state machine.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!scene || !canvas || !ready) return
    let downAt: { x: number; y: number } | null = null
    const onDown = (e: PointerEvent) => {
      downAt = { x: e.clientX, y: e.clientY }
    }
    const onUp = (e: PointerEvent) => {
      const start = downAt
      downAt = null
      if (!start || Math.hypot(e.clientX - start.x, e.clientY - start.y) > 6) return
      const pick = scene.pick(scene.pointerX, scene.pointerY)
      const artId = pick.hit ? (pick.pickedMesh?.metadata?.studioArtId as string | undefined) : undefined
      artsApi.select(artId || null)
      if (artId) {
        // Same "walk up and frame it" fly the STORY tour uses between
        // stops — a tap should feel like approaching the print, not just
        // toggling a highlight while the camera sits still.
        flyToArt(artId)
        // Guests have no Print Inspector to make "selection" meaningful on
        // its own (unlike owner Studio's select → separate expand action)
        // — go straight to the detail view in one tap.
        const idx = lightboxArts.findIndex((a) => a.id === artId)
        if (idx >= 0) setLightboxIndex(idx)
      }
      if (artId && role === 'guest' && !momentShownRef.current) {
        momentShownRef.current = true
        setMomentReason('print')
        setMoment(true)
      }
    }
    canvas.addEventListener('pointerdown', onDown)
    canvas.addEventListener('pointerup', onUp)
    return () => {
      canvas.removeEventListener('pointerdown', onDown)
      canvas.removeEventListener('pointerup', onUp)
    }
  }, [scene, ready, artsApi, role, lightboxArts, flyToArt])

  const goSignup = useCallback(() => navigate('/auth?step=signup'), [navigate])
  const goHome = useCallback(() => navigate('/'), [navigate])
  const goNewGallery = useCallback(() => navigate('/galleries'), [navigate])
  const goBackToStudio = useCallback(() => {
    if (gallery.topic) navigate(`/galleries/${gallery.topic.topicID}/studio`)
  }, [gallery.topic, navigate])

  const roomLabel = useMemo(() => {
    if (mode === 'classic') return getClassicFamilyForId(room.room.classicTemplateId ?? -1)?.label || 'Classic room'
    return SCENE_PRESET_GEOMETRY[room.room.scenePreset || 'corridor']?.label || 'Corridor'
  }, [mode, room.room.classicTemplateId, room.room.scenePreset])

  const tourArts = lightboxArts

  const onTourStep = useCallback(
    (art: { id: string; side: -1 | 0 | 1; w: number }) => {
      artsApi.select(art.id)
      flyToArt(art.id, art)
    },
    [artsApi, flyToArt],
  )

  const tour = useStudioTour(tourArts, room.room.dwellSeconds ?? 4, onTourStep)

  const storyBottom = role === 'guest' && !bannerDismissed ? 108 : 88

  const bindWalkPad = useCallback(
    (axis: 'forward' | 'strafe', value: number) =>
      ({
        onPointerDown: (e: React.PointerEvent) => {
          e.preventDefault()
          e.stopPropagation()
          ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
          cameraRig.setWalkPad(axis === 'forward' ? { forward: value } : { strafe: value })
        },
        onPointerUp: (e: React.PointerEvent) => {
          e.preventDefault()
          cameraRig.setWalkPad(axis === 'forward' ? { forward: 0 } : { strafe: 0 })
        },
        onPointerCancel: () => {
          cameraRig.setWalkPad(axis === 'forward' ? { forward: 0 } : { strafe: 0 })
        },
      }) as const,
    [cameraRig],
  )

  useEffect(() => {
    return () => cameraRig.setWalkPad({ forward: 0, strafe: 0 })
  }, [cameraRig])

  if (gallery.status === 'loading') {
    return <StudioLoadingVeil label='OPENING GALLERY…' />
  }

  if (gallery.status === 'not-found') {
    return (
      <Stage ref={containerRef}>
        <CenterState>
          <GuestGate
            eyebrow='Link unavailable'
            title="We couldn't open this gallery"
            description='The link may be broken, expired, or the gallery was removed.'
            onGoHome={goHome}
            onBrowse={() => navigate('/discover')}
          />
        </CenterState>
      </Stage>
    )
  }

  if (gallery.status === 'private') {
    return (
      <Stage ref={containerRef}>
        <CenterState>
          <GuestGate
            eyebrow='Private gallery'
            title="This one's locked"
            description="Its owner hasn't shared this gallery publicly — ask them for a fresh link, or check back later."
            onGoHome={goHome}
            onBrowse={() => navigate('/discover')}
          />
        </CenterState>
      </Stage>
    )
  }

  return (
    <Stage ref={containerRef}>
      <Canvas ref={canvasRef} id='guest-gallery-canvas' />

      {ready && guestPins.length > 0 && (
        <StudioOverlays
          projection={projection}
          selectedArt={null}
          getSelectedTagPos={() => null}
          pins={guestPins}
          getPinPos={getPinPos}
          showPins={showPins}
          showPresenceDemo={false}
          presencePos={presencePos}
          onPinReveal={onPinReveal}
          reveal='click'
        />
      )}

      {lightboxIndex != null && lightboxArts.length > 0 && (
        <StudioCanvasLightbox
          arts={lightboxArts}
          index={Math.min(lightboxIndex, lightboxArts.length - 1)}
          roomLabel={roomLabel}
          galleryTitle={gallery.galleryTitle}
          pins={lightboxPins}
          onIndexChange={(i) => {
            setLightboxIndex(i)
            const next = lightboxArts[i]
            if (next) artsApi.select(next.id)
          }}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      <TopBar>
        <TbLeft>
          <TbMark>
            <LogoMark size={16} />
          </TbMark>
          <Box minWidth={0}>
            <TbTitle sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{gallery.galleryTitle}</TbTitle>
            <TbSub>
              <TbSubStrong>{gallery.ownerName}&apos;s</TbSubStrong> gallery · {roomLabel}
            </TbSub>
          </Box>
        </TbLeft>
        <TbRight>
          {tourArts.length > 0 && (
            <TbPillBtn
              type='button'
              onClick={() => {
                setMoment(false)
                tour.play()
              }}
              aria-label={tour.isPlaying ? 'Pause story' : 'Play story'}
            >
              {tour.isPlaying ? '⏸ Story' : '▶ Story'}
            </TbPillBtn>
          )}
          <TbPill>{cameraRig.view === 'walk' ? '👁 Walk' : '◍ Orbit'}</TbPill>
        </TbRight>
      </TopBar>

      {tourArts.length > 0 && (
        <StudioTourBar
          variant='guest'
          bottom={storyBottom}
          arts={tourArts}
          activeIndex={tour.activeIndex}
          isPlaying={tour.isPlaying}
          onPlayToggle={() => {
            setMoment(false)
            tour.play()
          }}
          onJumpTo={(i) => {
            setMoment(false)
            tour.jumpTo(i)
          }}
        />
      )}

      <PresentHud>
        <span>
          {cameraRig.canWalk ? (
            <>
              <Kbd>DRAG</Kbd> LOOK ·{' '}
              <Box component='span' sx={{ '@media (hover: none), (pointer: coarse)': { display: 'none' } }}>
                <Kbd>W A S D</Kbd> WALK
              </Box>
              <Box component='span' sx={{ display: 'none', '@media (hover: none), (pointer: coarse)': { display: 'inline' } }}>
                PAD TO WALK
              </Box>
            </>
          ) : (
            <>
              <Kbd>DRAG</Kbd> ORBIT · <Kbd>PINCH</Kbd> ZOOM
            </>
          )}
        </span>
        <PresentHudSep />
        <PresentResetBtn type='button' aria-label='Reset camera to starting view' title='Reset camera' onClick={() => cameraRig.resetCamera()}>
          <ResetGlyph size={13} />
        </PresentResetBtn>
        {role === 'owner' && (
          <Box component='span' sx={{ cursor: 'pointer' }} onClick={goBackToStudio}>
            <Kbd>BACK TO STUDIO</Kbd>
          </Box>
        )}
      </PresentHud>

      {cameraRig.canWalk && cameraRig.view === 'walk' && (
        <WalkPad aria-label='Walk controls'>
          <span />
          <WalkBtn type='button' aria-label='Walk forward' {...bindWalkPad('forward', 1)}>
            ↑
          </WalkBtn>
          <span />
          <WalkBtn type='button' aria-label='Strafe left' {...bindWalkPad('strafe', -1)}>
            ←
          </WalkBtn>
          <WalkBtn type='button' aria-label='Walk back' {...bindWalkPad('forward', -1)}>
            ↓
          </WalkBtn>
          <WalkBtn type='button' aria-label='Strafe right' {...bindWalkPad('strafe', 1)}>
            →
          </WalkBtn>
        </WalkPad>
      )}

      {role === 'guest' && (
        <CtaBanner $hidden={bannerDismissed}>
          <CtaCopy>
            <CtaAvatar>{gallery.ownerName[0]?.toUpperCase() || '?'}</CtaAvatar>
            <Box minWidth={0}>
              <CtaTextTitle>This is {gallery.ownerName}&apos;s gallery</CtaTextTitle>
              <CtaTextSub sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Create your own free at {APP_NAME} — no card required
              </CtaTextSub>
            </Box>
          </CtaCopy>
          <Box display='flex' alignItems='center' gap={1} flexShrink={0}>
            <BtnDismiss aria-label='Dismiss' onClick={() => setBannerDismissed(true)}>
              ✕
            </BtnDismiss>
            <Button variant='primary' size='sm' onClick={goSignup}>
              Start free →
            </Button>
          </Box>
        </CtaBanner>
      )}

      {role === 'member' && (
        <QuietPillWrap>
          <QuietPill>
            <span>Made with {APP_NAME}</span>
            <Button variant='spectrum' size='sm' onClick={goNewGallery}>
              + New gallery
            </Button>
          </QuietPill>
        </QuietPillWrap>
      )}

      {role === 'owner' && (
        <QuietPillWrap>
          <QuietPill>
            👁&nbsp;Viewing as guests see it —{' '}
            <Box component='span' sx={{ color: STUDIO_ACCENT.gold, fontWeight: 700, cursor: 'pointer' }} onClick={goBackToStudio}>
              Back to Studio
            </Box>
          </QuietPill>
        </QuietPillWrap>
      )}

      {moment && (
        <MomentScrim onClick={() => setMoment(false)}>
          <MomentCard onClick={(e) => e.stopPropagation()}>
            <MomentClose aria-label='Close' onClick={() => setMoment(false)}>
              ✕
            </MomentClose>
            <MomentIcon aria-hidden>✦</MomentIcon>
            {momentReason === 'pin' ? (
              <>
                <MomentTitle>That note is {gallery.ownerName}&apos;s</MomentTitle>
                <MomentCopy>
                  Sign up free to leave your own notes on prints — in this gallery once you&apos;re connected, or on one
                  you build yourself.
                </MomentCopy>
                <MomentActions>
                  <Button variant='spectrum' onClick={goSignup}>
                    Sign up to leave notes →
                  </Button>
                  <Button variant='ghost' onClick={() => setMoment(false)}>
                    Keep exploring
                  </Button>
                </MomentActions>
              </>
            ) : (
              <>
                <MomentTitle>Designed in {APP_NAME} Studio</MomentTitle>
                <MomentCopy>
                  The room, the frames, the light — {gallery.ownerName} arranged this whole wall in a few minutes. Yours
                  could look like this too.
                </MomentCopy>
                <MomentActions>
                  <Button variant='spectrum' onClick={goSignup}>
                    Design your own, free →
                  </Button>
                  <Button variant='ghost' onClick={() => setMoment(false)}>
                    Keep exploring
                  </Button>
                </MomentActions>
              </>
            )}
          </MomentCard>
        </MomentScrim>
      )}
    </Stage>
  )
}
